import React, { useEffect, useState } from 'react';
import PackageManager from './PackageManager';
import {
  AlertCircle, CheckCircle, Copy, Lock, LogOut, Plus, Trash2,
  Users, ArrowDownCircle, Mail, Settings, Clock
} from 'lucide-react';
import {
  getPackages,
  getAllLeadBookers,
  saveLeadBookerOrder,
  saveLeadBooker,
  deleteLeadBooker as deleteLeadBookerApi,
  updateLeadBooker,
  saveTeam,
  saveTeamOrder,
  getTeamsByBooker,
  deleteTeam as deleteTeamApi,
  removePlayer,
  getBookerConfig,
  getGlobalConfig,
  saveBookerConfig,
  saveGlobalConfig,
  exportData,
  sendEmails,
  startExternalExport,
  getLatestExternalExports,
  resubmitExternalExportItem
} from './awsService';

const STORAGE_KEYS = {
  role: 'team-registration.role',
  bookerId: 'team-registration.booker-id',
  sessionExpiry: 'team-registration.session-expiry'
};

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const EXTERNAL_STATUS = {
  pending: { label: 'Pending', className: 'border-[#2a3a3d] bg-[#162022] text-[#7d8f86]' },
  sending: { label: 'Sending', className: 'border-[#665329] bg-[#2b2517] text-[#f0c27b]' },
  confirmed: { label: 'Confirmed', className: 'border-[#285344] bg-[#142821] text-[#68d0a1]' },
  unknown: { label: 'Unknown', className: 'border-[#6b4a2d] bg-[#2a1d15] text-[#f0c27b]' },
  rejected: { label: 'Rejected', className: 'border-[#633333] bg-[#2a1818] text-[#e58b8b]' }
};

const saveSession = (role, bookerId = null) => {
  const expiryTime = Date.now() + SESSION_DURATION_MS;
  localStorage.setItem(STORAGE_KEYS.role, role);
  localStorage.setItem(STORAGE_KEYS.sessionExpiry, expiryTime.toString());
  if (bookerId) {
    localStorage.setItem(STORAGE_KEYS.bookerId, bookerId.toString());
  }
};

const isSessionValid = () => {
  const expiry = localStorage.getItem(STORAGE_KEYS.sessionExpiry);
  if (!expiry) return false;
  return Date.now() < parseInt(expiry, 10);
};

const clearStoredSession = () => {
  localStorage.removeItem(STORAGE_KEYS.role);
  localStorage.removeItem(STORAGE_KEYS.bookerId);
  localStorage.removeItem(STORAGE_KEYS.sessionExpiry);
};

  const normalizeBookerId = (value) => {
    if (value === undefined || value === null) return value;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? value : numeric;
  };

  const normalizeLeadBookers = (payload) => {
    const rawList = Array.isArray(payload) ? payload : payload?.items || payload?.Items || [];
    return rawList.map(booker => {
      const normalizedId = normalizeBookerId(booker.id ?? booker.bookerId ?? booker.bookerID);
      const parsedMaxTeams = Number(booker.maxTeams ?? booker.maxTeamCount);
      return {
        active: booker.active ?? true,
        ...booker,
        id: normalizedId ?? Date.now(),
        code: booker.code || booker.bookerCode || booker.inviteCode || booker.accessCode,
        maxTeams: Number.isNaN(parsedMaxTeams) ? 5 : parsedMaxTeams
      };
    });
  };

export default function TeamRegistration() {
  const [urlBookerId] = useState(() => new URLSearchParams(window.location.search).get('booker'));
  const [userRole, setUserRole] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [dataLoaded, setDataLoaded] = useState(true);
  const [leadBookers, setLeadBookers] = useState([]);

  const [packages, setPackages] = useState([]);
  const [adminTab, setAdminTab] = useState('bookers');

  // Create Booker State
  const [newLeadBookerName, setNewLeadBookerName] = useState('');
  const [newLeadBookerPackage, setNewLeadBookerPackage] = useState('legacy-silver');
  useEffect(() => {
    if (!packages.some(pkg => pkg.id === newLeadBookerPackage)) {
      setNewLeadBookerPackage(packages[0]?.id || '');
    }
  }, [packages, newLeadBookerPackage]);

  const [leadBookersToCreate, setLeadBookersToCreate] = useState(1);
  const [maxTeamsPerBooker, setMaxTeamsPerBooker] = useState(5);

  const [, setApiUrl] = useState('https://api.example.com/registrations');
  const [bookersData, setBookersData] = useState({});
  const [externalExports, setExternalExports] = useState({});
  const [currentBooker, setCurrentBooker] = useState(null);
  const [leadBookerInput, setLeadBookerInput] = useState('');

  // Waiver State
  const [waiverText, setWaiverText] = useState('');
  const [showWaiverEditor, setShowWaiverEditor] = useState(false);
  const [editedWaiverText, setEditedWaiverText] = useState('');

  const maxPlayersPerTeam = 4;

  const createEmptyBookerData = () => ({
    teams: [],
    locked: false,
    submissionHistory: [],
    auditLog: []
  });

  const getBookerDataLocal = (bookerId) =>
    bookersData[bookerId] || createEmptyBookerData();

  const updateBookerDataLocal = (bookerId, data) => {
    setBookersData(prev => ({
      ...prev,
      [bookerId]: {
        ...createEmptyBookerData(),
        ...(prev[bookerId] || {}),
        ...data
      }
    }));
  };

  const extractTeams = (payload, bookerId) => {
    const rawTeams = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.teams)
        ? payload.teams
        : Array.isArray(payload?.Items)
          ? payload.Items
          : [];

    return rawTeams.map((team, index) => {
      const teamIdCandidate = team?.id ?? team?.teamId ?? team?.teamID ?? team?.team_id;
      const normalizedId = normalizeBookerId(teamIdCandidate);
      const fallbackId = `${bookerId || 'booker'}-team-${index}`;
      return {
        ...team,
        id: normalizedId ?? teamIdCandidate ?? fallbackId,
        players: Array.isArray(team?.players) ? team.players : []
      };
    });
  };

  const resolveLockedStatus = (configPayload, fallbackPayload) => {
    if (configPayload && typeof configPayload.locked === 'boolean') {
      return configPayload.locked;
    }
    if (fallbackPayload && typeof fallbackPayload.locked === 'boolean') {
      return fallbackPayload.locked;
    }
    return false;
  };

  const fetchBookerDetails = async (bookerId) => {
    try {
      const [teamsPayload, configPayload] = await Promise.all([
        getTeamsByBooker(bookerId).catch(() => null),
        getBookerConfig(bookerId).catch(() => null)
      ]);

      return {
        teams: extractTeams(teamsPayload, bookerId),
        locked: resolveLockedStatus(configPayload, teamsPayload)
      };
    } catch (error) {
      console.error('Failed to fetch booker details', bookerId, error);
      return createEmptyBookerData();
    }
  };

  const refreshBookerFromApi = async (bookerId) => {
    const details = await fetchBookerDetails(bookerId);
    updateBookerDataLocal(bookerId, details);
    return details;
  };

  const reloadBookers = async (loadDetails = true) => {
    try {
      const bookersPayload = await getAllLeadBookers();
      const normalizedBookers = normalizeLeadBookers(bookersPayload);
      const activeBookers = normalizedBookers.filter(b => b.active);

      setLeadBookers(activeBookers);

      if (loadDetails) {
        const detailPairs = await Promise.all(
          activeBookers.map(async (booker) => {
            const details = await fetchBookerDetails(booker.id);
            return [booker.id, details];
          })
        );
        setBookersData(Object.fromEntries(detailPairs));
      }

      if (currentBooker) {
        const updated = activeBookers.find(b => b.id === currentBooker.id);
        if (updated) setCurrentBooker(updated);
      }

      return activeBookers;
    } catch (error) {
      console.error('Failed to reload lead bookers', error);
      setMessage('Failed to load lead bookers from server');
      return [];
    }
  };

  const loadMinimalDataForLogin = async () => {
    try {
      const bookersPayload = await getAllLeadBookers();
      const normalizedBookers = normalizeLeadBookers(bookersPayload);
      const activeBookers = normalizedBookers.filter(b => b.active);
      setLeadBookers(activeBookers);
      setBookersData({});
      return activeBookers;
    } catch (error) {
      setMessage('Failed to load bookers');
      return [];
    }
  };

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      setDataLoaded(false);
      setLoading(true);
      setLoadingMessage('Loading application...');

      try {
        const storedRole = localStorage.getItem(STORAGE_KEYS.role);
        const storedBookerId = localStorage.getItem(STORAGE_KEYS.bookerId);

        if (isMounted && storedRole && isSessionValid()) {
          setUserRole(storedRole);
          if (storedRole === 'lead' && storedBookerId) {
            setLoadingMessage('Restoring your session...');
            const bookers = await reloadBookers(false);
            const booker = bookers.find(b => b.id?.toString() === storedBookerId);
            if (booker) {
              setLoadingMessage('Loading your booking data...');
              await refreshBookerFromApi(booker.id);
              setCurrentBooker(booker);
            } else {
              clearStoredSession();
              setUserRole(null);
              setMessage('Your booking is no longer available. Please log in again.');
              await loadMinimalDataForLogin();
            }
          } else if (storedRole === 'admin') {
            setLoadingMessage('Loading admin dashboard...');
            await reloadBookers(true);
          }
        } else if (storedRole && !isSessionValid()) {
          clearStoredSession();
          setMessage('Your session has expired. Please log in again.');
          await loadMinimalDataForLogin();
        } else {
          setLoadingMessage('');
          await loadMinimalDataForLogin();
        }

        if (isMounted) setPackages(await getPackages());
        const globalConfigPayload = await getGlobalConfig().catch(() => null);
        if (isMounted && globalConfigPayload) {
          if (globalConfigPayload.apiUrl) setApiUrl(globalConfigPayload.apiUrl);
          if (globalConfigPayload.waiverText) {
            setWaiverText(globalConfigPayload.waiverText);
            setEditedWaiverText(globalConfigPayload.waiverText);
          }
        }

        if (isMounted && urlBookerId) {
          const bookers = leadBookers.length > 0 ? leadBookers : await reloadBookers(false);
          const target = bookers.find(b => b.id?.toString() === urlBookerId);
          if (target) await refreshBookerFromApi(target.id);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Initial load failed', error);
          setMessage('Failed to load data from server');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setLoadingMessage('');
          setDataLoaded(true);
        }
      }
    };

    bootstrap();
    return () => { isMounted = false; };
  }, [urlBookerId]);

  // Refresh package choices for players who already have the signup page open.
  useEffect(() => {
    if (!urlBookerId) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const rows = normalizeLeadBookers(await getAllLeadBookers()).filter(b => b.active);
        if (!cancelled) {
          setLeadBookers(rows);
          setCurrentBooker(prev => prev ? rows.find(b => b.id === prev.id) || prev : prev);
        }
      } catch (error) { console.error('Failed to refresh package choices', error); }
    };
    const interval = setInterval(refresh, 10000);
    window.addEventListener('focus', refresh);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener('focus', refresh); };
  }, [urlBookerId]);

  // Auto-refresh for admin
  useEffect(() => {
    if (userRole !== 'admin') return;
    const AUTO_REFRESH_INTERVAL = 30000;
    const refreshInterval = setInterval(async () => {
      try {
        console.log('Auto-refreshing admin data...');
        await reloadBookers(true);
        setPackages(await getPackages());
      } catch (error) {
        console.error('Auto-refresh failed:', error);
      }
    }, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(refreshInterval);
  }, [userRole]);

  // External exports continue on the server; poll their compact per-team status.
  useEffect(() => {
    if (userRole !== 'admin') return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const jobs = await getLatestExternalExports();
        if (!cancelled) {
          setExternalExports(Object.fromEntries((jobs || []).map(job => [String(job.bookerId), job])));
        }
      } catch (error) {
        console.error('Failed to refresh external export status', error);
      }
    };
    refresh();
    const interval = setInterval(refresh, 2500);
    return () => { cancelled = true; clearInterval(interval); };
  }, [userRole]);

  const handleAdminLogin = async () => {
    const correctPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'admin123';
    if (adminPassword === correctPassword) {
      setLoading(true);
      setLoadingMessage('Loading admin dashboard...');
      try {
        await reloadBookers(true);
        setUserRole('admin');
        setMessage('Admin access granted');
        setAdminPassword('');
        saveSession('admin');
      } catch (error) {
        setMessage('Login successful but failed to load data');
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    } else {
      setMessage('Incorrect password');
    }
  };

  const generateLeadBookerCode = () => 'LB' + Math.random().toString(36).substr(2, 9).toUpperCase();

  const handleCreateLeadBookers = async () => {
    if (!newLeadBookerName.trim()) {
      setMessage('Please enter a Lead Booker name');
      return;
    }
    const num = parseInt(leadBookersToCreate);
    const maxTeams = parseInt(maxTeamsPerBooker);

    setLoading(true);
    try {
      const generateBookerId = () => `booker-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const payloads = Array.from({ length: num }).map((_, index) => ({
        id: generateBookerId(),
        name: num === 1 ? newLeadBookerName : `${newLeadBookerName} ${index + 1}`,
        code: generateLeadBookerCode(),
        maxTeams,
        createdAt: new Date().toISOString(),
        active: true,
        packageId: newLeadBookerPackage,
        bookingDate: '',
        arrivalTime: ''
      }));

      await Promise.all(payloads.map(saveLeadBooker));
      await reloadBookers();
      setNewLeadBookerName('');
      setLeadBookersToCreate(1);
      setMessage(`Created ${num} Lead Booker(s) successfully`);
    } catch (error) {
      setMessage(`Failed to create Lead Booker(s): ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRegistration = async (booker, shouldLock) => {
    setLoading(true);
    try {
      await saveBookerConfig(booker.id, { locked: shouldLock });
      await refreshBookerFromApi(booker.id);
      setMessage(`${shouldLock ? 'Closed' : 'Reopened'} registration for ${booker.name}`);
    } catch (error) {
      setMessage(`Failed to update registration: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Scoped Export (CSV)
  const handleExportDataTools = async (bookerId) => {
    setLoading(true);
    try {
      const data = await exportData(bookerId);
      if (!data || !data.length) {
        setMessage('No data to export');
        return;
      }
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(fieldName => {
          const val = row[fieldName] ? String(row[fieldName]).replace(/"/g, '""') : '';
          return `"${val}"`;
        }).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `export_${bookerId || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setMessage('Export successful');
    } catch (error) {
      console.error(error);
      setMessage('Failed to export');
    } finally {
      setLoading(false);
    }
  };

  // Scoped Emails
  const handleSendEmails = async (bookerId) => {
    if (!window.confirm('Send emails to team members?')) return;
    setLoading(true);
    try {
      const result = await sendEmails(bookerId);
      setMessage(`Emails sent: ${result.sent}, Failed: ${result.failed}`);
    } catch (error) {
      setMessage('Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  // External Create Teams (Azure)
  const handleCreateTeamsExternal = async (booker) => {
    if (!booker) return;
    const bookerData = getBookerDataLocal(booker.id);

    if (!bookerData.locked) {
      setMessage('Registration must be locked first');
      return;
    }
    if (!bookerData.teams.length) {
      setMessage('No teams to create');
      return;
    }

    const previousJob = externalExports[String(booker.id)];
    const confirmation = previousJob
      ? `Create all ${bookerData.teams.length} teams again? This booking already has export history and may create duplicates.`
      : `Create ${bookerData.teams.length} teams in external system?`;
    if (!window.confirm(confirmation)) return;

    setLoading(true);
    try {
      const job = await startExternalExport(booker.id);
      setExternalExports(prev => ({ ...prev, [String(booker.id)]: job }));
      setMessage(`Creating ${job.teams.length} teams. Progress will continue on the server.`);
    } catch (error) {
      setMessage(`Failed to start team creation: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmitExternalTeam = async (item) => {
    if (!window.confirm(`Resubmit ${item.teamName}? Check the external system first to avoid creating a duplicate.`)) return;
    setLoading(true);
    try {
      const job = await resubmitExternalExportItem(item.id);
      setExternalExports(prev => ({ ...prev, [String(job.bookerId)]: job }));
      setMessage(`Resubmitting ${item.teamName}`);
    } catch (error) {
      setMessage(`Failed to resubmit ${item.teamName}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const saveWaiver = async () => {
    setLoading(true);
    try {
      await saveGlobalConfig({ waiverText: editedWaiverText });
      setWaiverText(editedWaiverText);
      setMessage('Waiver saved successfully');
      setShowWaiverEditor(false);
    } catch (error) {
      setMessage('Failed to save waiver');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeadBooker = async (bookerId) => {
    if (!window.confirm('Are you sure you want to delete this Booker?')) return;
    setLoading(true);
    try {
      await deleteLeadBookerApi(bookerId);
      await reloadBookers();
      setMessage('Lead Booker deleted');
      if (localStorage.getItem(STORAGE_KEYS.bookerId) === bookerId?.toString()) {
        clearStoredSession();
        setCurrentBooker(null);
        setUserRole(null);
      }
    } catch (error) {
      setMessage(`Failed to delete: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLeadBookerLogin = async () => {
    const booker = leadBookers.find(b => b.code === leadBookerInput && b.active);
    if (booker) {
      setLoading(true);
      setLoadingMessage('Loading your booking data...');
      try {
        await refreshBookerFromApi(booker.id);
        setCurrentBooker(booker);
        setUserRole('lead');
        setMessage(`Welcome, ${booker.name}`);
        setLeadBookerInput('');
        saveSession('lead', booker.id);
      } catch (error) {
        setMessage(`Failed to load booking data: ${error.message}`);
      } finally {
        setLoading(false);
        setLoadingMessage('');
      }
    } else {
      setMessage('Incorrect or inactive Lead Booker code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage('Copied to clipboard');
  };

  const handleCreateTeam = async () => {
    if (!currentBooker) return;
    const bookerData = getBookerDataLocal(currentBooker.id);
    const newTeamNameInput = document.getElementById('newTeamName');
    const newTeamName = newTeamNameInput?.value || '';

    if (!newTeamName.trim()) {
      setMessage('Please enter a team name');
      return;
    }
    if (bookerData.locked) {
      setMessage('Registration is locked');
      return;
    }
    if (bookerData.teams.length >= currentBooker.maxTeams) {
      setMessage(`Maximum teams (${currentBooker.maxTeams}) reached`);
      return;
    }

    setLoading(true);
    try {
      const teamPayload = {
        id: `team-${Date.now()}`,
        name: newTeamName.trim(),
        players: [],
        createdAt: new Date().toISOString(),
        startTime: ''
      };
      await saveTeam(currentBooker.id, teamPayload);
      await refreshBookerFromApi(currentBooker.id);
      if (newTeamNameInput) newTeamNameInput.value = '';
      setMessage(`Team "${newTeamName}" created`);
    } catch (error) {
      setMessage(`Failed to create team: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTeamDetails = async (bookerId, teamId, updates, label) => {
    if (userRole !== 'admin') return;
    const bookerData = getBookerDataLocal(bookerId);
    const team = bookerData.teams.find(t => t.id === teamId);
    if (!team) return;

    setLoading(true);
    try {
      await saveTeam(bookerId, { ...team, ...updates });
      await refreshBookerFromApi(bookerId);
      setMessage(`${label} updated for ${team.name}`);
    } catch (err) {
      setMessage(`Failed to update ${label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBookerSchedule = async (bookerId, updates, label) => {
    if (userRole !== 'admin') return;
    const normalizeBookingDate = (value) => {
      const trimmed = (value || '').trim();
      if (!trimmed) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const [yyyy, mm, dd] = trimmed.split('-');
        return `${dd}/${mm}/${yyyy}`;
      }
      return trimmed;
    };
    const normalizedUpdates = { ...updates };
    if (Object.prototype.hasOwnProperty.call(normalizedUpdates, 'bookingDate')) {
      normalizedUpdates.bookingDate = normalizeBookingDate(normalizedUpdates.bookingDate);
    }
    setLoading(true);
    try {
      await updateLeadBooker(bookerId, normalizedUpdates);
      await reloadBookers(true);
      setMessage(`${label} updated`);
    } catch (err) {
      setMessage(`Failed to update ${label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveLeadBooker = async (bookerId, direction) => {
    if (loading || userRole !== 'admin') return;
    const index = leadBookers.findIndex(booker => booker.id === bookerId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= leadBookers.length) return;
    const ordered = [...leadBookers];
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    setLoading(true);
    try {
      await saveLeadBookerOrder(ordered.map(booker => String(booker.id)));
      setLeadBookers(ordered);
      setMessage('Lead booker order saved');
    } catch (error) {
      setMessage(`Failed to reorder lead bookers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveTeam = async (bookerId, teamId, direction) => {
    if (loading || !['admin', 'lead'].includes(userRole)) return;
    const teams = getBookerDataLocal(bookerId).teams;
    const index = teams.findIndex(team => team.id === teamId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= teams.length) return;
    const ordered = [...teams];
    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    setLoading(true);
    try {
      await saveTeamOrder(bookerId, ordered.map(team => team.id));
      await refreshBookerFromApi(bookerId);
      setMessage('Team order saved');
    } catch (error) {
      setMessage(`Failed to reorder teams: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const teamOrderControls = (bookerId, team, index, count) => (
    <div className="flex gap-2">
      {[-1, 1].map(direction => (
        <button
          key={direction}
          type="button"
          aria-label={`Move ${team.name} ${direction === -1 ? 'up' : 'down'}`}
          disabled={loading || (direction === -1 ? index === 0 : index === count - 1)}
          onClick={() => handleMoveTeam(bookerId, team.id, direction)}
          className="text-xs text-[#d3bf74] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {direction === -1 ? '↑ Move up' : '↓ Move down'}
        </button>
      ))}
    </div>
  );

  const handleDeleteTeam = async (teamId) => {
    if (!currentBooker) return;
    if (!window.confirm('Delete this team?')) return;
    setLoading(true);
    try {
      await deleteTeamApi(currentBooker.id, teamId);
      await refreshBookerFromApi(currentBooker.id);
      setMessage('Team deleted');
    } catch (error) {
      setMessage(`Failed to delete team: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayer = async (teamId, player) => {
    if (!currentBooker) return;
    const bookerData = getBookerDataLocal(currentBooker.id);
    if (bookerData.locked || loading) return;
    if (player.id == null) {
      setMessage('Cannot remove this player. Refresh the page and try again.');
      return;
    }
    if (!window.confirm(`Remove ${player.firstName} ${player.lastName} from this team? The team will be kept.`)) return;

    setLoading(true);
    try {
      await removePlayer(currentBooker.id, teamId, player.id);
      await refreshBookerFromApi(currentBooker.id);
      setMessage('Player removed');
    } catch (error) {
      setMessage(`Failed to remove player: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async () => {
    const normalizedUrlBookerId = urlBookerId ? normalizeBookerId(urlBookerId) : null;
    const activeBooker = currentBooker || leadBookers.find(
      b => b && b.id?.toString() === (normalizedUrlBookerId?.toString() || '')
    );

    if (!activeBooker) {
      setMessage('Booking not found');
      return;
    }

    const bookerData = getBookerDataLocal(activeBooker.id);

    const firstName = document.getElementById('firstName')?.value || '';
    const lastName = document.getElementById('lastName')?.value || '';
    const email = document.getElementById('email')?.value || '';
    const teamSelect = document.getElementById('teamSelect');
    const selectedTeamId = teamSelect?.value;
    const drinkSelect = document.getElementById('welcomeDrink');
    const drinkChoice = drinkSelect?.value || '';
    const foodChoice = document.getElementById('foodChoice')?.value || '';
    const packageOptions = activeBooker.packageSnapshot || { drinkOptions: [], foodOptions: [] };
    const dietaryInput = document.getElementById('dietaryRequirements');
    const dietaryValue = dietaryInput?.value || '';
    const waiverCheckbox = document.getElementById('waiverAccepted');
    const waiverAccepted = waiverCheckbox?.checked;

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setMessage('Please fill in all required fields');
      return;
    }
    if (!selectedTeamId) {
      setMessage('Please select a team');
      return;
    }
    if (packageOptions.drinkOptions.length && !packageOptions.drinkOptions.includes(drinkChoice)) {
      setMessage('Please select a welcome drink');
      return;
    }
    if (packageOptions.foodOptions.length && !packageOptions.foodOptions.includes(foodChoice)) {
      setMessage('Please select food');
      return;
    }
    if (!waiverAccepted) {
      setMessage('You must accept the waiver to join');
      return;
    }

    if (bookerData.locked) {
      setMessage('Registration is locked');
      return;
    }

    const team = bookerData.teams.find(t => t.id?.toString() === selectedTeamId);
    if (!team) {
      setMessage('Selected team not found');
      return;
    }
    if (team.players.length >= maxPlayersPerTeam) {
      setMessage('This team is full');
      return;
    }

    setLoading(true);
    try {
      const updatedTeam = {
        ...team,
        players: [
          ...team.players,
          {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            joinedAt: new Date().toISOString(),
            waiverSignedAt: new Date().toISOString(),
            drinkChoice: drinkChoice, // Store as drinkChoice to match schema
            welcomeDrink: drinkChoice, // Keep for backward compat if needed
            foodChoice,
            dietaryRequirements: dietaryValue.trim()
          }
        ]
      };
      await saveTeam(activeBooker.id, updatedTeam);
      await refreshBookerFromApi(activeBooker.id);

      // Clear form
      ['firstName', 'lastName', 'email', 'welcomeDrink', 'foodChoice', 'dietaryRequirements'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });
      if (teamSelect) teamSelect.value = '';
      if (waiverCheckbox) waiverCheckbox.checked = false;

      setMessage('Successfully joined team!');
    } catch (error) {
      setMessage(`Failed to join team: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --- VIEWS ---

  // WAIVER EDITOR MODAL
  const renderWaiverEditor = () => {
    if (!showWaiverEditor) return null;
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0d1416] border border-[#1f2f31] rounded-[28px] p-6 w-full max-w-2xl shadow-[0_30px_60px_rgba(0,0,0,0.7)]">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74] mb-4">Edit Waiver Text</h2>
          <textarea
            className="w-full h-64 bg-[#0d1618] border border-[#203235] rounded-[18px] p-4 text-white text-sm focus:outline-none resize-none mb-4"
            value={editedWaiverText}
            onChange={e => setEditedWaiverText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowWaiverEditor(false)}
              className="px-4 py-2 rounded-full border border-[#422727] bg-[#2a1818] text-[#f0c27b] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#3b1f1f]"
            >
              Cancel
            </button>
            <button
              onClick={saveWaiver}
              className="px-4 py-2 rounded-full border border-[#2a3a3d] bg-[#1a2729] text-[#68d0a1] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#233538]"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  // LOADING OVERLAY
  if (loading && loadingMessage) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0d1416] border border-[#1f2f31] rounded-[28px] p-8 flex flex-col items-center gap-4 shadow-[0_30px_60px_rgba(0,0,0,0.7)]">
          <div className="animate-spin h-12 w-12 border-4 border-[#d3bf74] border-t-transparent rounded-full"></div>
          <p className="text-sm text-[#7d8f86]">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  // PLAYER VIEW
  if (urlBookerId && dataLoaded) {
    const booker = leadBookers.find(b => b && b.active && b.id?.toString() === urlBookerId);
    if (!booker) {
      return (
        <div className="min-h-screen bg-[#0b0d10] px-6 py-12 text-gray-100 sm:px-8 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-[#f0c27b] mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-white">Invalid Booking Link</h1>
            <button
              onClick={() => window.location.href = window.location.pathname}
              className="mt-6 inline-flex items-center justify-center rounded-full border border-[#20393b] bg-[#162825] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#d3bf74] transition hover:border-[#d3bf74]"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    const bookerData = getBookerDataLocal(booker.id);
    const totalPlayers = bookerData.teams.reduce((sum, t) => sum + t.players.length, 0);
    const { drinkOptions = [], foodOptions = [] } = booker.packageSnapshot || {};

    return (
      <div className="min-h-screen bg-[#0b0d10] px-6 py-10 text-gray-100 sm:px-8">
        <div className="mx-auto max-w-4xl space-y-8">
          <div className="flex flex-col gap-4 border-b border-[#1f2f31] pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Team Registration</h1>
              <p className="mt-2 text-sm text-[#7d8f86]">
                Booking: <span className="font-semibold text-[#d3bf74]">{booker.name}</span>
              </p>
              {(booker.bookingDate || booker.arrivalTime) && (
                <div className="mt-2 text-xs text-[#d3bf74] space-y-1">
                  {booker.bookingDate && <div>Booking Date: {booker.bookingDate}</div>}
                  {booker.arrivalTime && <div>Arrival Time: {booker.arrivalTime}</div>}
                </div>
              )}
            </div>
            <button
              onClick={() => window.location.href = window.location.pathname}
              className="inline-flex items-center gap-2 rounded-full border border-[#422727] bg-[#2b1717] px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#f0c27b] transition hover:border-[#f0c27b] hover:text-[#ffe0a1]"
            >
              <LogOut className="h-4 w-4" /> Back
            </button>
          </div>

          {bookerData.locked && (
            <div className="flex items-center gap-3 rounded-[20px] border border-[#522d2d] bg-[#2a1818] px-5 py-4 text-sm text-[#f0c27b] shadow-[0_20px_45px_rgba(0,0,0,0.4)]">
              <Lock className="h-5 w-5" />
              <span>Registration for this booking is now closed.</span>
            </div>
          )}

          {!bookerData.locked && (
            <div className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] p-6 shadow-[0_24px_50px_rgba(0,0,0,0.5)]">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Join a Team</h2>
              <div className="mt-6 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">First Name *</label>
                    <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 shadow-inner shadow-[#0a0f11]">
                      <input id="firstName" type="text" placeholder="John" className="w-full bg-transparent text-sm text-white placeholder-[#445052] focus:outline-none" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Last Name *</label>
                    <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 shadow-inner shadow-[#0a0f11]">
                      <input id="lastName" type="text" placeholder="Smith" className="w-full bg-transparent text-sm text-white placeholder-[#445052] focus:outline-none" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Email Address *</label>
                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 shadow-inner shadow-[#0a0f11]">
                    <input id="email" type="email" placeholder="john@example.com" className="w-full bg-transparent text-sm text-white placeholder-[#445052] focus:outline-none" />
                  </div>
                </div>

                {drinkOptions.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Welcome Drink *</label>
                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-1 shadow-inner shadow-[#0a0f11]">
                    <select id="welcomeDrink" className="w-full bg-transparent px-2 py-2 text-sm text-white focus:outline-none">
                      <option value="" className="bg-[#0d1618] text-white">Select an option</option>
                      {drinkOptions.map(opt => (
                        <option key={opt} value={opt} className="bg-[#0d1618] text-white">{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
                )}

                {foodOptions.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Food *</label>
                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-1 shadow-inner shadow-[#0a0f11]">
                    <select id="foodChoice" className="w-full bg-transparent px-2 py-2 text-sm text-white focus:outline-none">
                      <option value="" className="bg-[#0d1618] text-white">Select an option</option>
                      {foodOptions.map(opt => (
                        <option key={opt} value={opt} className="bg-[#0d1618] text-white">{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
                )}

                <div className="space-y-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Dietary Requirements (Optional)</label>
                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 shadow-inner shadow-[#0a0f11]">
                    <textarea id="dietaryRequirements" rows="2" placeholder="Allergies, etc." className="w-full resize-none bg-transparent text-sm text-white placeholder-[#445052] focus:outline-none" />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Select Team *</label>
                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-1 shadow-inner shadow-[#0a0f11]">
                    <select id="teamSelect" className="w-full bg-transparent px-2 py-2 text-sm text-white focus:outline-none">
                      <option value="" className="bg-[#0d1618] text-white">{bookerData.teams.length === 0 ? 'No teams available' : 'Choose a team...'}</option>
                      {bookerData.teams.map(t => (
                        <option key={t.id} value={t.id} disabled={t.players.length >= maxPlayersPerTeam} className="bg-[#0d1618] text-white">
                          {t.name} ({t.players.length}/{maxPlayersPerTeam}){t.players.length >= maxPlayersPerTeam ? ' - FULL' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Waiver Section */}
                <div className="space-y-2 pt-2">
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86]">Waiver *</label>
                  <div className="h-32 overflow-y-auto rounded-[18px] border border-[#203235] bg-[#0d1618] p-4 text-xs text-gray-400">
                    {waiverText || 'No waiver text configured.'}
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer mt-2 group">
                    <div className="relative flex items-center">
                      <input type="checkbox" id="waiverAccepted" className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-[#203235] bg-[#0d1618] checked:border-[#d3bf74] checked:bg-[#d3bf74] transition-all" />
                      <CheckCircle className="absolute left-0.5 top-0.5 h-4 w-4 text-black opacity-0 peer-checked:opacity-100 pointer-events-none" />
                    </div>
                    <span className="text-sm text-[#7d8f86] group-hover:text-white transition-colors">I accept the terms of the waiver</span>
                  </label>
                </div>

                <button
                  onClick={handleJoinTeam}
                  disabled={bookerData.teams.length === 0 || loading}
                  className="w-full rounded-full border border-[#20393b] bg-[#162825] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#d3bf74] transition hover:border-[#d3bf74] hover:text-[#f7df92] disabled:cursor-not-allowed disabled:opacity-50 mt-4"
                >
                  Join Team
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Teams</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {bookerData.teams.map(team => (
                <div key={team.id} className="flex h-full flex-col gap-4 rounded-[24px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] p-6 shadow-[0_20px_45px_rgba(0,0,0,0.45)]">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-white">{team.name}</h3>
                    <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] ${team.players.length >= maxPlayersPerTeam ? 'bg-[#5a3a2b] text-[#f2c47b]' : 'bg-[#1f4a3c] text-[#68d0a1]'}`}>
                      {team.players.length}/{maxPlayersPerTeam}
                    </span>
                  </div>
                  <div className="space-y-2 overflow-y-auto rounded-[18px] border border-[#203235] bg-[#0d1618] p-4 shadow-inner shadow-[#0a0f11]">
                    {team.players.map((player, idx) => (
                      <div key={idx} className="text-sm text-white">
                        <p className="font-semibold">{player.firstName} {player.lastName}</p>
                      </div>
                    ))}
                    {team.players.length === 0 && <div className="text-center text-sm text-[#566462]">No players yet</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {message && (
            <div className={`flex items-center gap-3 rounded-[20px] border px-5 py-4 text-sm shadow-[0_20px_45px_rgba(0,0,0,0.4)] ${message.includes('success') ? 'border-[#1f3a33] bg-[#132521] text-[#6fd0a4]' : 'border-[#522d2d] bg-[#2a1818] text-[#f0c27b]'}`}>
              <span>{message}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // LOGIN PAGE
  if (!userRole) {
    return (
      <div className="min-h-screen bg-[#0b0d10] px-6 py-14 text-gray-100 sm:px-8">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-10">
          <header className="text-center space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Escape Arcade Team Registration</h1>
            <p className="text-sm text-[#7d8f86]">Authenticate as an administrator or lead booker.</p>
          </header>

          <div className="grid gap-6 md:grid-cols-2">
            <section className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0d1416] p-6 shadow-[0_24px_50px_rgba(0,0,0,0.6)]">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Admin Access</h2>
              <form className="mt-6 space-y-4" onSubmit={e => { e.preventDefault(); handleAdminLogin(); }}>
                <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 shadow-inner shadow-[#0a0f11]">
                  <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} placeholder="Enter admin password" className="w-full bg-transparent text-sm text-white placeholder-[#445052] focus:outline-none" />
                </div>
                <button type="submit" className="w-full rounded-full border border-[#20393b] bg-[#162825] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#d3bf74] transition hover:border-[#d3bf74] hover:text-[#f7df92]">Enter Dashboard</button>
              </form>
            </section>

            <section className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1315] p-6 shadow-[0_24px_50px_rgba(0,0,0,0.55)]">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Lead Booker</h2>
              <form className="mt-6 space-y-4" onSubmit={e => { e.preventDefault(); handleLeadBookerLogin(); }}>
                <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 shadow-inner shadow-[#0a0f11]">
                  <input type="text" value={leadBookerInput} onChange={e => setLeadBookerInput(e.target.value)} placeholder="Enter booking code" className="w-full bg-transparent text-sm text-white placeholder-[#445052] focus:outline-none" />
                </div>
                <button type="submit" className="w-full rounded-full border border-[#d3bf74] bg-[#d3a23d] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-black transition hover:brightness-110">Enter Booking</button>
              </form>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // ADMIN DASHBOARD
  if (userRole === 'admin') {
    return (
      <div className="min-h-screen bg-[#0b0d10] px-6 py-10 text-gray-100 sm:px-8">
        {renderWaiverEditor()}
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-8">
          <div className="flex flex-col gap-4 border-b border-[#1f2f31] pb-6 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-3xl font-semibold tracking-tight text-white md:text-4xl">Admin Dashboard</h1>
            <div className="flex gap-2">
              <button onClick={() => setShowWaiverEditor(true)} className="inline-flex items-center gap-2 rounded-full border border-[#2a3a3d] bg-[#1a2729] px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#68d0a1] transition hover:border-[#68d0a1] hover:text-[#8ce8bb]">
                <Settings className="h-4 w-4" /> Waiver
              </button>
              <button onClick={() => { setUserRole(null); clearStoredSession(); }} className="inline-flex items-center gap-2 rounded-full border border-[#422727] bg-[#2b1717] px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#f0c27b] transition hover:border-[#f0c27b] hover:text-[#ffe0a1]">
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </div>
          </div>

          <nav className="flex gap-3" aria-label="Admin sections">
            {['bookers', 'packages'].map(tab => <button key={tab} onClick={() => setAdminTab(tab)} aria-pressed={adminTab === tab} className={`rounded-full border px-5 py-3 text-sm ${adminTab === tab ? 'border-[#d3bf74] text-[#d3bf74]' : 'border-[#203235] text-[#7d8f86]'}`}>{tab === 'bookers' ? 'Lead Bookers' : 'Packages'}</button>)}
          </nav>
          {adminTab === 'packages' ? <PackageManager packages={packages} onCreated={pkg => { setPackages(prev => [...prev, pkg]); setNewLeadBookerPackage(pkg.id); }} onUpdated={pkg => setPackages(prev => prev.map(item => item.id === pkg.id ? pkg : item))} onDeleted={id => setPackages(prev => prev.filter(pkg => pkg.id !== id))} /> : <>
          {/* CREATE BOOKER */}
          <section className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0d1416] p-6 shadow-[0_30px_60px_rgba(0,0,0,0.55)]">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Create Lead Bookers</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-4 items-end">
              <div className="sm:col-span-1">
                <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86] mb-1">Name</label>
                <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3">
                  <input value={newLeadBookerName} onChange={e => setNewLeadBookerName(e.target.value)} placeholder="Event Name" className="w-full bg-transparent text-sm text-white focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86] mb-1">Count</label>
                <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3">
                  <input type="number" value={leadBookersToCreate} onChange={e => setLeadBookersToCreate(e.target.value)} className="w-full bg-transparent text-sm text-white focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86] mb-1">Max Teams</label>
                <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3">
                  <input type="number" value={maxTeamsPerBooker} onChange={e => setMaxTeamsPerBooker(e.target.value)} className="w-full bg-transparent text-sm text-white focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.35em] text-[#7d8f86] mb-1">Package</label>
                <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-1">
                  <select value={newLeadBookerPackage} onChange={e => setNewLeadBookerPackage(e.target.value)} className="w-full bg-transparent text-sm text-white py-2 focus:outline-none">
                    {packages.map(pkg => <option key={pkg.id} value={pkg.id} className="bg-[#0d1618]">{pkg.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <button disabled={loading || !packages.length} onClick={handleCreateLeadBookers} className="mt-4 w-full rounded-full border border-[#d3bf74] bg-[#d3a23d] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-black hover:brightness-110">
              Create
            </button>
          </section>

          {/* BOOKER LIST */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
            {leadBookers.map((booker, index) => {
              const bookerData = getBookerDataLocal(booker.id);
              const externalJob = externalExports[String(booker.id)];
              const externalTeams = externalJob?.teams || [];
              const externalActive = externalTeams.some(item => item.status === 'pending' || item.status === 'sending');
              const confirmedTeams = externalTeams.filter(item => item.status === 'confirmed').length;
              return (
                <div key={booker.id} className="flex flex-col gap-4 rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1315] p-6 shadow-lg">
                  <div className="flex justify-between gap-2">
                    {[-1, 1].map(direction => (
                      <button
                        key={direction}
                        type="button"
                        aria-label={`Move lead booker ${booker.name} ${direction === -1 ? 'up' : 'down'}`}
                        disabled={loading || (direction === -1 ? index === 0 : index === leadBookers.length - 1)}
                        onClick={() => handleMoveLeadBooker(booker.id, direction)}
                        className="text-xs text-[#d3bf74] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {direction === -1 ? '↑ Move up' : '↓ Move down'}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold text-white">{booker.name}</h3>
                      <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#7d8f86] cursor-pointer" onClick={() => copyToClipboard(booker.code)}>{booker.code}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${booker.package === 'Gold' ? 'bg-yellow-900 text-yellow-200' : 'bg-gray-800 text-gray-300'}`}>
                        {booker.package}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${bookerData.locked ? 'text-red-400' : 'text-green-400'}`}>{bookerData.locked ? 'Closed' : 'Open'}</span>
                    </div>
                  </div>

                  <div className="text-xs text-[#7d8f86] flex flex-col gap-1">
                    <span>Max Teams: <span className="text-white">{booker.maxTeams}</span></span>
                    <span>Total Players: <span className="text-white">{bookerData.teams.reduce((sum, t) => sum + t.players.length, 0)}</span></span>
                  </div>

                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] p-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#7d8f86] mb-2">Booking Details</p>
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        className="bg-[#162825] border border-[#20393b] text-white text-xs px-2 py-1 rounded focus:outline-none"
                        defaultValue={booker.bookingDate}
                        placeholder="dd/mm/yyyy"
                        inputMode="numeric"
                        onBlur={e => handleUpdateBookerSchedule(booker.id, { bookingDate: e.target.value, arrivalTime: booker.arrivalTime || '' }, 'Booking date')}
                      />
                      <input
                        type="time"
                        className="bg-[#162825] border border-[#20393b] text-white text-xs px-2 py-1 rounded focus:outline-none"
                        defaultValue={booker.arrivalTime}
                        onBlur={e => handleUpdateBookerSchedule(booker.id, { bookingDate: booker.bookingDate || '', arrivalTime: e.target.value }, 'Arrival time')}
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => handleExportDataTools(booker.id)} className="flex-1 rounded-full border border-[#2a3a3d] bg-[#1a2729] py-2 text-[10px] uppercase tracking-widest text-[#68d0a1] flex items-center justify-center gap-1 hover:bg-[#233538]">
                      <ArrowDownCircle size={12} /> CSV
                    </button>
                    <button onClick={() => handleSendEmails(booker.id)} className="flex-1 rounded-full border border-[#2a3a3d] bg-[#1a2729] py-2 text-[10px] uppercase tracking-widest text-[#61dafb] flex items-center justify-center gap-1 hover:bg-[#233538]">
                      <Mail size={12} /> Email
                    </button>
                    <button
                      onClick={() => handleCreateTeamsExternal(booker)}
                      disabled={!bookerData.locked || externalActive || loading}
                      className={`flex-1 rounded-full border py-2 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1 ${bookerData.locked && !externalActive
                        ? 'border-[#2a3a3d] bg-[#1a2729] text-[#f0c27b] hover:bg-[#233538]'
                        : 'border-[#2a1818] bg-[#1a1212] text-[#422727] cursor-not-allowed'
                        }`}
                    >
                      <Users size={12} /> {externalActive ? 'Sending' : externalJob ? 'Create again' : 'Create'}
                    </button>
                  </div>

                  {externalJob && (
                    <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] p-3">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7d8f86]">External creation</p>
                        <span className="text-[10px] text-[#566462]">{confirmedTeams}/{externalTeams.length} confirmed</span>
                      </div>
                      <div className="max-h-36 space-y-1.5 overflow-y-auto pr-1">
                        {externalTeams.map(item => {
                          const status = EXTERNAL_STATUS[item.status] || EXTERNAL_STATUS.pending;
                          return (
                            <div key={item.id} className="flex items-center gap-2 rounded-xl border border-[#18282a] bg-[#10191b] px-2.5 py-2" title={item.error || ''}>
                              <span className="min-w-0 flex-1 truncate text-xs text-white">{item.teamName}</span>
                              {item.attemptCount > 1 && <span className="text-[9px] text-[#566462]">Try {item.attemptCount}</span>}
                              <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${status.className}`}>
                                {item.status === 'confirmed' ? <CheckCircle size={10} /> : item.status === 'unknown' ? <AlertCircle size={10} /> : item.status === 'sending' ? <Clock size={10} /> : null}
                                {status.label}
                              </span>
                              {item.status === 'unknown' && (
                                <button
                                  type="button"
                                  aria-label={`Resubmit ${item.teamName}`}
                                  disabled={loading}
                                  onClick={() => handleResubmitExternalTeam(item)}
                                  className="rounded-full border border-[#6b4a2d] px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-[#f0c27b] transition hover:border-[#f0c27b] hover:text-[#ffe0a1] disabled:opacity-40"
                                >
                                  Resubmit
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Team Start Times */}
                  <div className="rounded-[18px] border border-[#203235] bg-[#0d1618] p-3 max-h-40 overflow-y-auto">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#7d8f86] mb-2">Team Start Times</p>
                    {bookerData.teams.length === 0 && <p className="text-xs text-gray-600 italic">No teams yet</p>}
                    {bookerData.teams.map((t, index) => (
                      <div key={t.id} className="flex flex-wrap gap-2 justify-between items-center mb-3">
                        <span className="text-xs text-white truncate w-32" title={t.name}>
                          {t.name} <span className="text-[#7d8f86]">({t.players.length}/{maxPlayersPerTeam})</span>
                        </span>
                        {teamOrderControls(booker.id, t, index, bookerData.teams.length)}
                        <input
                          type="time"
                          className="bg-[#162825] border border-[#20393b] text-white text-xs px-1 rounded focus:outline-none"
                          defaultValue={t.startTime}
                          onBlur={e => handleUpdateTeamDetails(booker.id, t.id, { startTime: e.target.value }, 'Start time')}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto flex flex-wrap gap-2 pt-2 border-t border-[#1f2f31]">
                    <button onClick={() => copyToClipboard(`${window.location.origin}?booker=${booker.id}`)} className="text-[#d3bf74] text-xs uppercase tracking-wider hover:text-[#fff]">Link</button>
                    <button onClick={() => handleToggleRegistration(booker, !bookerData.locked)} className="text-[#7d8f86] text-xs uppercase tracking-wider hover:text-white ml-auto">{bookerData.locked ? 'Unlock' : 'Lock'}</button>
                    <button onClick={() => handleDeleteLeadBooker(booker.id)} className="text-red-500 text-xs uppercase tracking-wider hover:text-red-400">Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
          </>}
          {message && <p role="status" className="text-sm text-[#d3bf74]">{message}</p>}
        </div>
      </div>
    );
  }

  // LEAD BOOKER DASHBOARD (Detailed)
  if (userRole === 'lead' && currentBooker) {
    const bookerData = getBookerDataLocal(currentBooker.id);
    const totalPlayers = bookerData.teams.reduce((sum, t) => sum + t.players.length, 0);
    const registrationUrl = `${window.location.origin}${window.location.pathname}?booker=${currentBooker.id}`;

    return (
      <div className="min-h-screen bg-[#0b0d10] px-6 py-10 text-gray-100 sm:px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="flex flex-col gap-4 border-b border-[#1f2f31] pb-6 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight text-white md:text-4xl">
              <Users className="h-7 w-7 text-[#d3bf74]" /> Lead Booker Dashboard
            </h1>
            <button
              onClick={() => {
                setUserRole(null);
                setCurrentBooker(null);
                clearStoredSession();
              }}
              className="inline-flex items-center gap-2 rounded-full border border-[#422727] bg-[#2b1717] px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#f0c27b] transition hover:border-[#f0c27b] hover:text-[#ffe0a1]"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>

          {bookerData.locked && (
            <div className="flex items-center gap-3 rounded-[20px] border border-[#522d2d] bg-[#2a1818] px-5 py-4 text-sm text-[#f0c27b] shadow-[0_20px_45px_rgba(0,0,0,0.4)]">
              <Lock className="h-5 w-5" /> Registration is LOCKED - No changes allowed
            </div>
          )}

          <div className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] p-6 shadow-[0_24px_50px_rgba(0,0,0,0.5)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">
              Booking Overview
            </p>
            <p className="mt-3 text-sm text-[#7d8f86]">
              Booking: <span className="font-semibold text-white">{currentBooker.name}</span>
            </p>
            <p className="mt-2 text-xs text-[#7d8f86]">
              Code: <code className="font-mono text-[#d3bf74]">{currentBooker.code}</code>
            </p>
            <p className="mt-2 text-xs text-[#7d8f86]">Your Registration URL:</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={registrationUrl}
                readOnly
                className="flex-1 rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 text-xs font-mono text-white shadow-inner shadow-[#0a0f11]"
              />
              <button
                onClick={() => copyToClipboard(registrationUrl)}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#20393b] bg-[#162825] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#d3bf74] transition hover:border-[#d3bf74] hover:text-[#f7df92]"
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-[24px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] px-5 py-4 shadow-[0_18px_40px_rgba(0,0,0,0.45)]">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#7d8f86]">Teams Created</p>
              <p className="mt-3 text-3xl font-semibold text-white">{bookerData.teams.length}</p>
              <p className="mt-2 text-xs text-[#566462]">Max: {currentBooker.maxTeams}</p>
            </div>
            <div className="rounded-[24px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] px-5 py-4 shadow-[0_18px_40px_rgba(0,0,0,0.45)]">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#7d8f86]">Total Players</p>
              <p className="mt-3 text-3xl font-semibold text-white">{totalPlayers}</p>
              <p className="mt-2 text-xs text-[#566462]">Capacity: {currentBooker.maxTeams * maxPlayersPerTeam}</p>
            </div>
            <div className="rounded-[24px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] px-5 py-4 shadow-[0_18px_40px_rgba(0,0,0,0.45)]">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#7d8f86]">Progress</p>
              <p className="mt-3 text-3xl font-semibold text-white">
                {Math.round((bookerData.teams.length / currentBooker.maxTeams) * 100)}%
              </p>
              <div className="mt-3 h-2 w-full rounded-full bg-[#1a2729]">
                <div
                  className="h-2 rounded-full bg-[#d3bf74]"
                  style={{ width: `${Math.round((bookerData.teams.length / currentBooker.maxTeams) * 100)}%` }}
                />
              </div>
            </div>
            <div className="rounded-[24px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] px-5 py-4 shadow-[0_18px_40px_rgba(0,0,0,0.45)]">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#7d8f86]">Registration</p>
              <p className={`mt-3 text-2xl font-semibold ${bookerData.locked ? 'text-[#f0c27b]' : 'text-[#68d0a1]'}`}>
                {bookerData.locked ? '🔒 Locked' : '🔓 Open'}
              </p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] p-6 shadow-[0_24px_50px_rgba(0,0,0,0.5)]">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Create Teams</h2>
              {!bookerData.locked && (
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <input
                    id="newTeamName"
                    type="text"
                    placeholder="Enter team name..."
                    disabled={bookerData.teams.length >= currentBooker.maxTeams || loading}
                    className="flex-1 rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 text-sm text-white shadow-inner shadow-[#0a0f11] placeholder-[#445052] focus:outline-none disabled:opacity-50"
                  />
                  <button
                    onClick={handleCreateTeam}
                    disabled={bookerData.teams.length >= currentBooker.maxTeams || loading}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[#20393b] bg-[#162825] px-5 py-3 text-xs font-semibold uppercase tracking-[0.35em] text-[#d3bf74] transition hover:border-[#d3bf74] hover:text-[#f7df92] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" /> Create
                  </button>
                </div>
              )}
              {bookerData.locked && (
                <p className="mt-4 text-xs text-[#7d8f86]">
                  Unlock registrations to create new teams.
                </p>
              )}
            </div>

            <div className="rounded-[28px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] p-6 shadow-[0_24px_50px_rgba(0,0,0,0.5)]">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74]">Status</h2>
              <div className="mt-4 rounded-[18px] border border-[#203235] bg-[#0d1618] px-4 py-3 text-center text-sm text-white shadow-inner shadow-[#0a0f11]">
                {bookerData.locked ? '🔒 Locked' : '🔓 Open'}
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#d3bf74] mb-4">Teams</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {bookerData.teams.map((team, index) => (
                <div
                  key={team.id}
                  className="flex h-full flex-col gap-4 rounded-[24px] border border-[#1f2f31] bg-gradient-to-br from-[#111a1c] via-[#111a1c] to-[#0a1416] p-6 shadow-[0_20px_45px_rgba(0,0,0,0.45)]"
                >
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-white">{team.name}</h3>
                    {!bookerData.locked && (
                      <button
                        type="button"
                        aria-label={`Delete team ${team.name}`}
                        onClick={() => handleDeleteTeam(team.id)}
                        disabled={loading}
                        className="text-[#f0c27b] transition hover:text-[#ffe0a1] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {teamOrderControls(currentBooker.id, team, index, bookerData.teams.length)}
                  <p className="text-sm text-[#7d8f86]">{team.players.length}/{maxPlayersPerTeam} Players</p>
                  <div className="space-y-2 overflow-y-auto rounded-[18px] border border-[#203235] bg-[#0d1618] p-4 shadow-inner shadow-[#0a0f11]">
                    {team.players.map((player, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm text-white">
                        <span>{player.firstName} {player.lastName}</span>
                        {!bookerData.locked && (
                          <button
                            type="button"
                            onClick={() => handleRemovePlayer(team.id, player)}
                            disabled={loading}
                            className="text-[#f0c27b] transition hover:text-[#ffe0a1] text-xs disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    {team.players.length === 0 && (
                      <div className="text-center text-sm text-[#566462]">No players yet</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {message && (
            <div
              className={`flex items-center gap-3 rounded-[20px] border px-5 py-4 text-sm shadow-[0_20px_45px_rgba(0,0,0,0.4)] ${message.includes('❌') ||
                message.includes('Cannot') ||
                message.includes('Failed') ||
                message.includes('full') ||
                message.includes('invalid')
                ? 'border-[#522d2d] bg-[#2a1818] text-[#f0c27b]'
                : 'border-[#1f3a33] bg-[#132521] text-[#6fd0a4]'
                }`}
            >
              {message.includes('❌') ||
                message.includes('Cannot') ||
                message.includes('Failed') ||
                message.includes('full') ||
                message.includes('invalid') ? (
                <AlertCircle className="h-5 w-5" />
              ) : (
                <CheckCircle className="h-5 w-5" />
              )}
              <span>{message}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback return if no role matched (should be covered by !userRole check, but good safety)
  return null;
}
